/**
 * English message catalog — the source of truth for message keys.
 *
 * Every other locale is typed as a partial of this object, so adding a key here
 * makes its absence elsewhere visible to the coverage report rather than
 * silently rendering a raw key to a user (§25).
 *
 * Strings the directive specifies verbatim (§11 status, §13 edit notice, §34
 * errors) are reproduced exactly. Do not paraphrase them.
 */
export const en = {
  /* ---- product ---- */
  'app.name': 'Modu Viewer',
  'app.tagline': 'Open, view, and edit your files instantly—without ads.',
  'app.home.primary': 'Open received files instantly—without ads.',

  /* ---- home screen (§10) ---- */
  'home.openFile': 'Open File',
  'home.recentFiles': 'Recent Files',
  'home.favorites': 'Favorites',
  'home.workInProgress': 'Work in Progress',
  'home.recoverable': 'Recoverable Files',
  'home.browseDevice': 'Browse Device',
  'home.settings': 'Settings',

  /* ---- progressive preview status (§11) ---- */
  'status.checkingFile': 'Checking the file',
  'status.preparingPreview': 'Preparing a safe preview',
  'status.firstPageReady': 'The first page is ready',
  'status.loadingRemaining': 'Loading the remaining content',
  'status.readyToEdit': 'Ready to edit',
  'status.limitedSupport': 'This file has limited support',
  'status.mayBeDamaged': 'The file may be damaged or incomplete',
  'status.originalUnchanged': 'The original file has not been changed',
  'status.savedAs': 'Saved as a new file',
  'status.extracted': 'Extracted from the archive and saved',

  /* ---- errors (§34) ---- */
  'error.unableToRead': 'Unable to read this file.',
  'error.damagedOrIncomplete': 'The file may be damaged or incomplete.',
  'error.passwordProtected': 'This file is password protected.',
  'error.featuresUnsupported': 'Some features are not supported for this file.',
  'error.permissionExpired': 'File access permission has expired.',
  'error.reopenFromSource': 'Open the file again from the original application.',
  'error.notEnoughStorage': 'There is not enough storage space.',
  'error.saveFailed': 'The file could not be saved.',
  'error.previewFailed': 'Preview generation failed.',
  'error.editsSavedTemporarily': 'Your edits were saved temporarily.',
  'error.originalUnchanged': 'The original file has not been changed.',
  'error.mustSaveAsNewFile': 'The edited copy must be saved as a new file.',
  'error.formattingMayNotBePreserved': 'Some advanced formatting may not be preserved.',
  'error.formulaNotRecalculated': 'This formula cannot currently be recalculated.',
  'error.conversionRequired': 'This document requires conversion before editing.',

  /* ---- actions (§34) ---- */
  'action.tryAgain': 'Try Again',
  'action.saveOriginal': 'Save Original',
  'action.openInAnotherApp': 'Open in Another App',
  'action.recover': 'Recover',
  'action.saveAsNewFile': 'Save as New File',
  'action.reportError': 'Report Error',
  'action.close': 'Close',
  'action.cancel': 'Cancel',
  'action.edit': 'Edit',
  'action.share': 'Share',
  'action.print': 'Print',
  'action.showDetails': 'Show details',

  /* ---- editing (§13) ---- */
  'edit.originalProtected': 'The original file will not be changed. Your edits will be saved as a new file.',
  'edit.confirmOverwriteTitle': 'Replace the original file?',
  'edit.confirmOverwriteBody': 'This cannot be undone. Saving as a new file keeps the original intact.',
  'edit.fileExistsTitle': 'A file with this name already exists',
  'edit.fileExistsBody': 'Choose a different name, or confirm that you want to replace it.',
  'edit.lossWarningTitle': 'Some features may not be preserved',
  'edit.lossWarningBody': 'Saving in this format can lose the following: {items}',
  'edit.autoSavedAt': 'Auto-saved at {time}',
  'edit.saveFailedCopyKept': 'Saving failed. Your editing copy has been kept.',
  'edit.recoveryAvailable': 'Unsaved edits from {time} were recovered.',

  /* ---- detection warnings (§30) ---- */
  'detect.warning.emptyFile': 'This file is empty.',
  'detect.warning.shortHead': 'Only part of the file has been read so far.',
  'detect.warning.noSignature': 'This file type could not be identified from its contents.',
  'detect.warning.extensionMismatch': 'The file extension does not match the actual contents.',
  'detect.warning.mimeMismatch': 'The file type reported by the sending app does not match the contents.',
  'detect.warning.notTextDespiteExtension': 'The extension suggests text, but the contents are binary.',
  'detect.warning.unsupportedFormat': 'This file type is not supported yet.',
  'detect.warning.containerUnresolved': 'The contents of this container could not be identified.',
  'detect.warning.macrosBlocked': 'This file contains macros. They are kept in the file but never run.',
  'detect.warning.encrypted': 'This file is encrypted.',
  'detect.warning.sensitiveContent': 'This file may contain credentials. It is excluded from recent files, thumbnails and error reports.',
  'detect.warning.executableContent': 'This archive contains executable files. Modu Viewer never runs them and never opens them.',

  /* ---- archive safety (§21) ---- */
  'archive.violation.unsafePath': 'An entry in this archive would be extracted outside the destination folder.',
  'archive.violation.tooManyEntries': 'This archive contains an unusually large number of files ({count}).',
  'archive.violation.expansionTooLarge': 'Extracting this archive would use {size} of storage.',
  'archive.violation.ratioTooHigh': 'An entry expands far beyond its compressed size, which can indicate a malicious archive.',
  'archive.violation.encryptedEntry': 'This archive is password protected.',
  'archive.violation.zip64Unsupported': 'This archive uses a format extension that is not supported yet.',
  'archive.masqueradeWarning': 'This entry looks like a document but is an executable file.',

  /* ---- file information screen (§31) ---- */
  'fileInfo.title': 'File information',
  'fileInfo.fileName': 'File name',
  'fileInfo.fileType': 'File type',
  'fileInfo.detectedMime': 'Detected MIME type',
  'fileInfo.fileSize': 'File size',
  'fileInfo.originalOrCopy': 'Original or edited copy',
  'fileInfo.readOnly': 'Read-only',
  'fileInfo.editSupportLevel': 'Editing support',
  'fileInfo.viewSupportLevel': 'Viewing support',
  'fileInfo.transmittedExternally': 'Sent outside this device',
  'fileInfo.processedLocally': 'Processed on this device',
  'fileInfo.macrosBlocked': 'Macros or scripts blocked',
  'fileInfo.accessSource': 'Opened from',
  'fileInfo.tempLocation': 'Temporary copy location',
  'fileInfo.cacheDeletionTime': 'Temporary copy is deleted',
  'fileInfo.lastSaved': 'Last saved',
  'fileInfo.saveResult': 'Save result',
  'fileInfo.validationResult': 'File check result',
  'fileInfo.passwordStatus': 'Password or encryption',
  'fileInfo.unsupportedSummary': 'Features not supported',
  'fileInfo.value.yes': 'Yes',
  'fileInfo.value.no': 'No',
  'fileInfo.value.original': 'Original',
  'fileInfo.value.editedCopy': 'Edited copy',
  'fileInfo.value.never': 'Never',
  'fileInfo.value.unknown': 'Not known',

  /* ---- support levels (§6) ---- */
  'support.full-view': 'Full viewing',
  'support.standard-view': 'Standard viewing',
  'support.text-view': 'Text-focused viewing',
  'support.full-edit': 'Full editing',
  'support.basic-edit': 'Basic editing',
  'support.annotate-edit': 'Annotation editing',
  'support.convert-edit': 'Editing after conversion',
  'support.read-only': 'Read-only',
  'support.needs-validation': 'Technical validation required',
  'support.commercial-license': 'Commercial license required',
  'support.unsupported': 'Not supported',

  /* ---- viewer controls (§12) ---- */
  'viewer.zoomIn': 'Zoom in',
  'viewer.zoomOut': 'Zoom out',
  'viewer.fitWidth': 'Fit to width',
  'viewer.fitPage': 'Fit to page',
  'viewer.actualSize': 'Actual size',
  'viewer.fullScreen': 'Full screen',
  'viewer.search': 'Search',
  'viewer.previousResult': 'Previous result',
  'viewer.nextResult': 'Next result',
  'viewer.rotate': 'Rotate',
  'viewer.page': 'Page {current} of {total}',
  'viewer.sheet': 'Sheet',
  'viewer.slide': 'Slide {current} of {total}',
  'viewer.paragraph': 'Paragraph',
  'viewer.entry': 'Entry',
  'viewer.undo': 'Undo',
  'viewer.redo': 'Redo',
  'viewer.darkMode': 'Dark mode',

  /* ---- privacy (§29, §31) ---- */
  'privacy.localOnly': 'This file was processed entirely on this device.',
  'privacy.noUpload': 'Nothing was sent to a server.',
  'privacy.consentRequired': 'This action sends the file to a server. It will not proceed without your consent.',
  'privacy.cacheCleared': 'Temporary files were deleted.',
} as const;

/** Every message key in the product. */
export type MessageKey = keyof typeof en;

/** A complete catalog. Only the fallback locale is required to be complete. */
export type MessageCatalog = Readonly<Record<MessageKey, string>>;

/** A translation in progress. Missing keys fall back to English (§25). */
export type PartialMessageCatalog = Readonly<Partial<Record<MessageKey, string>>>;

export const MESSAGE_KEYS = Object.keys(en) as readonly MessageKey[];
